import {momentDeLaJournee} from "../ambiance";

/**
 * Parcours du fond « Oiseau » (dans l'esprit de Flappy Bird) : où sont les tuyaux, et par où l'oiseau passe. Aucun dessin ici,
 * seulement du calcul (donc testable).
 *
 * Les tuyaux sont posés sur une bande qui se répète tous les PERIODE pixels et défile à vitesse constante. L'oiseau reste à
 * XOISEAU ; il passe par le centre de l'ouverture de chaque tuyau au moment où ce tuyau arrive à sa hauteur. Cet instant, en
 * part de la durée de la boucle, ne dépend que de la géométrie (pas de la vitesse) : les images clés du vol sont donc les
 * mêmes à toutes les vitesses, seule la durée change.
 */

export const HAUTEUR = 112;          // hauteur du bandeau
export const SOL = 14;               // hauteur du sol
export const NOMBRE = 12;            // tuyaux par période
export const ESPACEMENT = 220;       // distance entre deux tuyaux
export const PERIODE = NOMBRE * ESPACEMENT;   // 2 640 px : plus large que les plus grands bandeaux (2 560 px)
export const X0 = 100;               // position du premier tuyau dans la bande
export const XOISEAU = 300;          // position de l'oiseau (loin du logo AkitaTek, quelle que soit la largeur du bandeau)
export const HAUTEUR_OISEAU = 16;
export const LARGEUR_CORPS = 24;      // largeur du corps d'un tuyau
export const LARGEUR_EMBOUT = 28;      // largeur de son embout
export const LARGEUR_OISEAU = 24;
export const SAUT_MAXIMUM = 30;      // plus grand écart de hauteur entre deux ouvertures voisines

/** Générateur pseudo-aléatoire à graine (mulberry32). */
function aleatoire(graine) {
    let a = graine >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Position horizontale du tuyau n° k dans la bande. */
export const xTuyau = k => X0 + k * ESPACEMENT;

/** Hauteurs possibles du centre d'une ouverture de taille `ouverture` : ni collée au plafond ni au sol. */
export function bornesCentre(ouverture) {
    const marge = 10;
    return {min: marge + ouverture / 2, max: HAUTEUR - SOL - marge - ouverture / 2};
}

/**
 * Hauteur du centre de l'ouverture de chaque tuyau : une houle (nombre entier de cycles, donc qui se referme) plus un peu de
 * hasard, ramenée dans les bornes, et jamais plus de SAUT_MAXIMUM d'un tuyau au suivant (dernier et premier compris).
 */
export function centres(ouverture) {
    const {min, max} = bornesCentre(ouverture);
    const milieu = (min + max) / 2, amplitude = (max - min) / 2 * 0.85;
    const r = aleatoire(20261007);
    const phase = r() * Math.PI * 2;
    const brut = Array.from({length: NOMBRE}, (_, k) =>
        milieu + amplitude * Math.sin((2 * Math.PI * 2 * k) / NOMBRE + phase) + (r() - 0.5) * 8);
    return brut.map(c => Math.max(min, Math.min(max, c)));
}

/**
 * Passages de l'oiseau, un par tuyau, dans l'ordre où ils arrivent à sa hauteur pendant une boucle :
 * [{part, y}] où `part` (0 à 1, exclu) est la part de la boucle écoulée et `y` la hauteur du centre de l'ouverture.
 */
export function passages(liste) {
    return liste
        .map((y, k) => {
            const distance = ((xTuyau(k) - XOISEAU) % PERIODE + PERIODE) % PERIODE;
            return {part: distance / PERIODE, y};
        })
        .sort((a, b) => a.part - b.part);
}

/** Sauts entre deux ouvertures voisines : chacun est un coup d'aile (montée rapide) puis une chute en arc. */
export const SAUTS_PAR_TRAJET = 4;
/** Instant du sommet d'un saut, en part de sa durée : tôt, car la montée est vive et la chute plus longue. */
export const SOMMET = 0.3;
/** Hauteur maximale d'un saut près d'un tuyau, au-dessus de la ligne droite qui relie deux ouvertures (pixels). */
export const SAUT_HAUTEUR_MAXIMALE = 10;
/** Hauteur des sauts du milieu du trajet, là où il n'y a pas de tuyau : de grands sauts bien visibles. */
export const SAUT_LIBRE = 15;
/** Instants échantillonnés dans chaque saut (en part de sa durée) : le sommet y est, pour que la pointe de l'arc soit exacte. */
export const ECHANTILLONS = [0, 0.1, 0.2, SOMMET, 0.45, 0.6, 0.75, 0.9];

/** Profil d'un saut : 0 au départ, 1 au sommet, 0 à l'arrivée ; une parabole de chaque côté du sommet. */
export function profilSaut(tau) {
    const demi = tau < SOMMET ? SOMMET : 1 - SOMMET;
    return 1 - Math.pow((tau - SOMMET) / demi, 2);
}

/**
 * Hauteur d'un saut selon l'ouverture : l'oiseau reste dans l'ouverture pendant tout le passage du tuyau, qui est large. Plus
 * l'ouverture est serrée, plus les sauts sont petits (au minimum 2 px, une simple ondulation).
 */
export function hauteurSaut(ouverture) {
    const marge = ouverture / 2 - HAUTEUR_OISEAU / 2 - 2;      // jeu de chaque côté de l'oiseau
    return Math.max(2, Math.min(SAUT_HAUTEUR_MAXIMALE, marge - 5.5));
}

/**
 * Le vol de l'oiseau : { y(part), parts } où y donne la hauteur du centre à un instant de la boucle (part de 0 à 1) et `parts`
 * les instants qui décrivent toutes les courbes (à utiliser comme images clés, la courbe entre deux étant presque droite).
 * Entre deux passages l'oiseau fait SAUTS_PAR_TRAJET sauts sur la droite qui relie les deux ouvertures ; il est exactement au
 * centre de l'ouverture à chaque passage (début d'un saut). Le premier et le dernier saut, qui touchent les tuyaux, sont petits
 * (hauteurSaut) ; ceux du milieu, libres, sont grands (SAUT_LIBRE).
 */
export function vol(liste, ouverture) {
    const p = passages(liste), n = p.length, m = SAUTS_PAR_TRAJET, petit = hauteurSaut(ouverture);
    const hauteurDuSaut = j => (j === 0 || j === m - 1 ? petit : SAUT_LIBRE);
    const trajet = i => ({
        debut: p[i].part,
        fin: i < n - 1 ? p[i + 1].part : p[0].part + 1,
        de: p[i].y,
        a: p[(i + 1) % n].y,
    });

    const y = part => {
        const u = part >= p[0].part ? part : part + 1;
        let i = n - 1;
        while (i > 0 && p[i].part > u) i--;
        const t = trajet(i), tau = Math.min(1 - 1e-12, (u - t.debut) / (t.fin - t.debut)) * m;
        const j = Math.floor(tau), local = tau - j;
        const ys = t.de + (t.a - t.de) * (j / m), ye = t.de + (t.a - t.de) * ((j + 1) / m);
        return ys + (ye - ys) * local - hauteurDuSaut(j) * profilSaut(local);
    };

    const parts = new Set([0, 1]);
    for (let i = 0; i < n; i++) {
        const t = trajet(i);
        for (let j = 0; j < m; j++) {
            ECHANTILLONS.forEach(tau => parts.add(((t.debut + ((j + tau) / m) * (t.fin - t.debut)) % 1 + 1) % 1));
        }
    }
    return {y, parts: [...parts].sort((a, b) => a - b)};
}

/**
 * Images clés (texte CSS) du vol, à lire en ligne droite (animation-timing-function: linear) : l'oiseau fait des sauts en arc
 * d'une ouverture à l'autre. La boucle se referme : l'image de 100 % est celle de 0 %.
 */
export function imagesClesVol(nom, liste, ouverture) {
    const {y, parts} = vol(liste, ouverture);
    const cles = parts.map(part => `${(part * 100).toFixed(4)}%{transform:translate3d(0,${(y(part === 1 ? 0 : part) - HAUTEUR_OISEAU / 2).toFixed(1)}px,0)}`);
    return `@keyframes ${nom}{${cles.join("")}}`;
}

/** Ambiance à afficher : celle demandée, ou celle de l'heure si « auto » (horaires réglés dans le tableau de bord : voir ../ambiance.js). */
export const ambianceChoisie = (valeur, date, horaires) => (valeur === "auto" ? momentDeLaJournee(date, horaires) : valeur);

/** Durée de la boucle (secondes) à la vitesse donnée en pixels par seconde. */
export const dureeBoucle = vitesse => PERIODE / vitesse;
